import React from 'react'

import { pick } from '@eastcoal/common/utils/object'

import {
  $applyNodeReplacement,
  addClassNamesToElement,
  DecoratorNode,
  DOMConversionMap,
  DOMExportOutput,
  EditorConfig,
  LexicalEditor,
  LexicalNode,
  NodeKey
} from '@eastcoal/editor/exports'
import { setDomStyle, styleText2Css } from '@eastcoal/editor/utils/style'

import { TextVariablePlaceholder } from '../../ui/TextVariablePlaceholder'

import {
  SerializedTextVariableNode,
  TextVariableNodeProps,
  TextVariableNodeState
} from './interface'

export class TextVariableNode extends DecoratorNode<React.JSX.Element> {
  private __variable: TextVariableNodeState['variable']
  private __styleConfig: TextVariableNodeState['styleConfig']

  constructor({ variable, styleConfig }: TextVariableNodeProps, key?: NodeKey) {
    super(key)
    this.__variable = variable
    this.__styleConfig = styleConfig
  }

  getVariable() {
    return this.__variable
  }

  getStyleConfig() {
    return this.__styleConfig || {}
  }

  setStyleConfig(styleConfig: TextVariableNodeState['styleConfig']) {
    this.getWritable().__styleConfig = Object.assign(
      {},
      this.__styleConfig,
      styleConfig
    )
  }

  static getType(): string {
    return 'text-variable'
  }

  static clone(node: TextVariableNode) {
    return new TextVariableNode(
      { variable: node.getVariable(), styleConfig: node.getStyleConfig() },
      node.getKey()
    )
  }

  static importJSON(
    serializedNode: SerializedTextVariableNode
  ): TextVariableNode {
    return $createTextVariableNode(serializedNode).updateFromJSON(
      serializedNode
    )
  }

  exportJSON(): SerializedTextVariableNode {
    return {
      ...super.exportJSON(),
      variable: this.getVariable(),
      styleConfig: this.getStyleConfig()
    }
  }

  static importDOM(): DOMConversionMap {
    return {
      span: () => ({
        priority: 0,
        conversion: dom => {
          if (
            dom.dataset.variableType === this.getType() &&
            dom.dataset.variable
          ) {
            return {
              node: $createTextVariableNode({
                variable: JSON.parse(dom.dataset.variable),
                styleConfig: pick(styleText2Css(dom.getAttribute('style')), [
                  'fontSize',
                  'fontWeight',
                  'textDecoration',
                  'color',
                  'backgroundColor',
                  'lineHeight'
                ])
              })
            }
          }
          return null
        }
      })
    }
  }

  exportDOM(editor: LexicalEditor): DOMExportOutput {
    const dom = document.createElement('span')
    const variable = this.getVariable()
    const style = this.getStyleConfig()
    // 跟 importDOM 一致，避免数据丢失
    // 以后需要复制粘贴功能再放开
    // dom.dataset.nodeType = this.getType()
    // dom.dataset.variable = JSON.stringify(variable)
    // 默认展示的内容，可有可无
    dom.innerHTML = `${variable.name}`
    // 设置样式
    setDomStyle(dom, style)
    // thymeleaf 模板语法，给后端渲染
    dom.setAttribute('th:text', `\${${variable.property}}`)
    return {
      element: dom
    }
  }

  updateDOM(
    _prevNode: unknown,
    _dom: HTMLElement,
    _config: EditorConfig
  ): boolean {
    return false
  }

  createDOM(config: EditorConfig, editor?: LexicalEditor): HTMLElement {
    const dom = document.createElement('span')
    addClassNamesToElement(dom, config.theme.textVariable)
    return dom
  }

  decorate(): React.JSX.Element {
    return (
      <TextVariablePlaceholder
        nodeKey={this.getKey()}
        styleConfig={this.getStyleConfig()}
        variable={this.getVariable()}
      />
    )
  }

  isInline(): boolean {
    return true
  }

  isKeyboardSelectable(): boolean {
    return true
  }

  isIsolated(): boolean {
    return false
  }
}

export const $isTextVariableNode = (
  node: LexicalNode | null
): node is TextVariableNode => {
  return node instanceof TextVariableNode
}

export const $createTextVariableNode = (props: TextVariableNodeProps) => {
  return $applyNodeReplacement(new TextVariableNode(props))
}
